import React, { useState, useEffect } from 'react';
import { ArrowLeft, Eye, Shield, Crown, Plane, Globe, BarChart3, Users, Zap, Lock } from 'lucide-react';
import { useSearchParams, useNavigate } from 'react-router-dom';

const ElitePortfolioPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('dashboard');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedFeature, setSelectedFeature] = useState<any>(null);
  const [modalType, setModalType] = useState<'none' | 'feature' | 'simulation' | 'analytics'>('none');

  const portfolioType = searchParams.get('type') || 'elite';

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  const eliteFeatures = [
    {
      id: 'intelligence',
      icon: <Eye className="w-8 h-8" />,
      title: 'Ultra-Elite Passenger Intelligence Dashboard',
      subtitle: 'Confidential Real-Time UHNWI Analytics',
      description: 'Access exclusive intelligence on royal family travel patterns, Fortune 500 executive routes, billionaire lifestyle segmentation, and preferred luxury destinations. This data is never publicly available.',
      features: [
        'Royal family travel patterns',
        'Fortune 500 executive routes', 
        'Billionaire lifestyle segmentation',
        'Preferred destinations and aviation corridors',
        'Peak luxury travel seasons'
      ],
      confidential: true,
      image: 'https://public.readdy.ai/ai/img_res/131ed9b0ce916d97b69100db75f57510.jpg'
    },
    {
      id: 'hologram',
      icon: <Zap className="w-8 h-8" />,
      title: 'In-Cabin Immersive 3D Advertising Experience',
      subtitle: 'World\'s First Holographic Jet Marketing',
      description: 'Revolutionary 3D hologram advertising inside private jets with ceiling-mounted projections, seat-back hologram messages, and AI-based passenger recognition for personalized experiences.',
      features: [
        'Ceiling-mounted 3D projections',
        'Seat-back hologram messages',
        'AI-based passenger recognition',
        'Personalized ad experiences',
        'No global competitor offers this technology'
      ],
      confidential: false,
      image: 'https://readdy.ai/api/search-image?query=Futuristic%203D%20holographic%20advertising%20display%20inside%20luxury%20private%20jet%20cabin%2C%20ceiling-mounted%20hologram%20projections%2C%20personalized%20AI-driven%20marketing%20messages%2C%20ultra-modern%20aircraft%20interior%20with%20advanced%20technology%20integration&width=800&height=500&seq=hologram-jet-advertising-2024&orientation=landscape'
    },
    {
      id: 'lounges',
      icon: <Shield className="w-8 h-8" />,
      title: 'Exclusive Access to 150+ "No-Public-Entry" VVIP Lounges',
      subtitle: 'Ultra-Secure Elite Aviation Terminals',
      description: 'Showcase your brand in high-security lounges never accessible to the public, including royal terminals, secret aviation suites, diplomatic corridors, and CEO-only boarding gates.',
      features: [
        'Royal terminals',
        'Secret aviation suites',
        'Diplomatic corridors',
        'CEO-only boarding gates',
        '99.99% of the world cannot enter'
      ],
      confidential: true,
      image: 'https://readdy.ai/api/search-image?query=Ultra-exclusive%20VIP%20aviation%20lounge%20with%20royal%20terminal%20access%2C%20secret%20diplomatic%20corridors%2C%20CEO-only%20boarding%20gates%2C%20high-security%20luxury%20environment%20with%20gold%20and%20marble%20finishes%2C%20confidential%20elite%20travel%20spaces&width=800&height=500&seq=exclusive-vip-lounges-2024&orientation=landscape'
    },
    {
      id: 'takeover',
      icon: <Crown className="w-8 h-8" />,
      title: 'Private Jet Fleet Media Takeover',
      subtitle: 'Full Cabin Brand Immersion at 45,000 Feet',
      description: 'Complete brand takeover including LED cabin ceilings, window shades, cabin mood lighting in brand colors, signature scent marketing, and luxury welcome kits.',
      features: [
        'LED cabin ceilings',
        'Custom window shades',
        'Brand-colored mood lighting',
        'Signature scent marketing',
        'Luxury welcome kits'
      ],
      confidential: false,
      image: 'https://readdy.ai/api/search-image?query=Luxury%20private%20jet%20cabin%20completely%20branded%20with%20LED%20ceiling%20displays%2C%20custom%20mood%20lighting%2C%20branded%20window%20shades%2C%20premium%20welcome%20kits%2C%20full%20brand%20immersion%20experience%20at%20high%20altitude%20with%20sophisticated%20interior%20design&width=800&height=500&seq=jet-brand-takeover-2024&orientation=landscape'
    },
    {
      id: 'mapping',
      icon: <Globe className="w-8 h-8" />,
      title: 'Live Jet-Route Advertising Map',
      subtitle: 'Real-Time Elite Audience Exposure Analytics',
      description: 'Dynamic real-time map showing active flight paths, regions where your brand is displayed, estimated elite audience exposure, and jet-by-jet analytics.',
      features: [
        'Active flight paths of private jets',
        'Real-time brand display regions',
        'Estimated elite audience exposure',
        'Jet-by-jet analytics',
        'Measurable advertising impact'
      ],
      confidential: false,
      image: 'https://readdy.ai/api/search-image?query=Advanced%20real-time%20global%20map%20showing%20private%20jet%20flight%20paths%2C%20luxury%20brand%20advertising%20coverage%2C%20elite%20audience%20analytics%2C%20sophisticated%20data%20visualization%20with%20golden%20flight%20routes%20and%20premium%20interface%20design&width=800&height=500&seq=live-jet-route-map-2024&orientation=landscape'
    },
    {
      id: 'gallery',
      icon: <BarChart3 className="w-8 h-8" />,
      title: 'World\'s First "Floating VIP Gallery"',
      subtitle: 'Cinematic 8K Luxury Showcase',
      description: 'Immersive cinematic showcase of private jet interiors, ultra-luxury cabins, high-end brand installations, and 8K slow-motion visuals in a floating gallery with parallax effects.',
      features: [
        'Private jet interiors showcase',
        'Ultra-luxury cabin displays',
        'High-end brand installations',
        '8K slow-motion visuals',
        'Floating gallery with parallax effects'
      ],
      confidential: false,
      image: 'https://readdy.ai/api/search-image?query=Cinematic%20floating%20gallery%20showcasing%20ultra-luxury%20private%20jet%20interiors%2C%208K%20high-definition%20cabin%20displays%2C%20premium%20brand%20installations%2C%20sophisticated%20parallax%20visual%20effects%2C%20museum-quality%20aviation%20luxury%20presentation&width=800&height=500&seq=floating-vip-gallery-2024&orientation=landscape'
    },
    {
      id: 'celebrity',
      icon: <Users className="w-8 h-8" />,
      title: 'Confidential Celebrity & UHNWI Engagement Program',
      subtitle: 'Discreet Elite Network Connections',
      description: 'Confidential service connecting brands with global celebrities, billionaire family offices, luxury investors, and top-tier influencers, all handled with NDAs and anonymized introductions.',
      features: [
        'Global celebrities',
        'Billionaire family offices',
        'Luxury investors',
        'Top-tier influencers',
        'NDA-protected anonymized introductions'
      ],
      confidential: true,
      image: 'https://readdy.ai/api/search-image?query=Confidential%20luxury%20networking%20environment%20with%20silhouettes%20of%20celebrities%20and%20billionaires%2C%20exclusive%20private%20aviation%20lifestyle%2C%20discreet%20elite%20connections%2C%20sophisticated%20dark%20ambiance%20with%20gold%20accents%20and%20privacy%20elements&width=800&height=500&seq=celebrity-uhnwi-program-2024&orientation=landscape'
    }
  ];

  const brandFeatures = [
    {
      id: 'heatmap',
      icon: <Globe className="w-8 h-8" />,
      title: 'Brand Exposure Heat-Map in Private Aviation',
      subtitle: 'Interactive Global Reach Visualization',
      description: 'Interactive map showing your brand\'s reach inside 500+ private jets, 50+ VVIP lounges, and 24+ elite global airports with color-coded exposure levels.',
      features: [
        '500+ private jets coverage',
        '50+ VVIP lounges',
        '24+ elite global airports',
        'Color-coded exposure levels',
        'Domination zone analysis'
      ],
      confidential: false,
      image: 'https://readdy.ai/api/search-image?query=Interactive%20global%20heat%20map%20showing%20luxury%20brand%20exposure%20across%20private%20aviation%20network%2C%20color-coded%20coverage%20zones%2C%20elite%20airport%20locations%2C%20sophisticated%20data%20visualization%20with%20premium%20gold%20and%20blue%20interface%20design&width=800&height=500&seq=brand-exposure-heatmap-2024&orientation=landscape'
    },
    {
      id: 'ai-matching',
      icon: <Zap className="w-8 h-8" />,
      title: 'AI-Optimized Luxury Audience Matching',
      subtitle: 'Precision VVIP Targeting System',
      description: 'Proprietary system that analyzes brand identity, matches with ideal VVIP profiles, predicts conversion potential, and recommends exact jet routes for maximum impact.',
      features: [
        'Brand identity analysis',
        'Ideal VVIP profile matching',
        'Conversion potential prediction',
        'Exact jet route recommendations',
        'Precision luxury targeting'
      ],
      confidential: false,
      image: 'https://readdy.ai/api/search-image?query=Advanced%20AI%20interface%20analyzing%20luxury%20brand%20identity%20and%20VVIP%20audience%20matching%2C%20sophisticated%20algorithm%20visualization%2C%20precision%20targeting%20system%2C%20futuristic%20data%20analysis%20with%20elegant%20gold%20and%20silver%20design%20elements&width=800&height=500&seq=ai-luxury-matching-2024&orientation=landscape'
    },
    {
      id: 'simulation',
      icon: <Eye className="w-8 h-8" />,
      title: 'Cinematic Brand Display Simulation',
      subtitle: '8K Virtual Cabin Demo Experience',
      description: 'Stunning 8K simulation showing how your brand appears inside private jets, on LED cabin ceilings, seat screens, luxury welcome kits, and terminal digital boards.',
      features: [
        'LED cabin ceiling displays',
        'Seat screen integration',
        'Luxury welcome kit branding',
        'Terminal digital board placement',
        '8K cinematic quality'
      ],
      confidential: false,
      image: 'https://readdy.ai/api/search-image?query=Ultra-high-definition%208K%20simulation%20of%20luxury%20brand%20display%20inside%20private%20jet%20cabin%2C%20LED%20ceiling%20branding%2C%20seat%20screen%20integration%2C%20premium%20welcome%20kits%2C%20cinematic%20quality%20visualization%20with%20sophisticated%20lighting&width=800&height=500&seq=brand-simulation-8k-2024&orientation=landscape'
    },
    {
      id: 'concierge',
      icon: <Crown className="w-8 h-8" />,
      title: 'Private Jet Concierge Activation Program',
      subtitle: 'Crew-Delivered Brand Experiences',
      description: 'Unique service where flight attendants deliver curated brand experiences, VIP passengers receive exclusive samples, and jet crews act as brand ambassadors.',
      features: [
        'Curated brand experience delivery',
        'Exclusive VIP sample distribution',
        'Integrated concierge scripting',
        'Crew brand ambassador training',
        'Unprecedented aviation marketing'
      ],
      confidential: false,
      image: 'https://readdy.ai/api/search-image?query=Elegant%20private%20jet%20flight%20attendant%20delivering%20luxury%20brand%20experience%20to%20VIP%20passenger%2C%20exclusive%20sample%20presentation%2C%20sophisticated%20cabin%20service%2C%20premium%20brand%20activation%20with%20high-end%20aviation%20hospitality&width=800&height=500&seq=concierge-brand-activation-2024&orientation=landscape'
    },
    {
      id: 'placement',
      icon: <Shield className="w-8 h-8" />,
      title: 'Invitation-Only Brand Placement Zones',
      subtitle: 'Access to Invisible Elite Locations',
      description: 'Exclusive brand placement in royal family rest areas, high-level diplomatic zones, elite CEO-only lounges, and private boarding suites normally invisible to the public.',
      features: [
        'Royal family rest areas',
        'High-level diplomatic zones',
        'Elite CEO-only lounges',
        'Private boarding suites',
        'Invisible to 99.99% of public'
      ],
      confidential: true,
      image: 'https://readdy.ai/api/search-image?query=Ultra-exclusive%20royal%20family%20rest%20area%20with%20luxury%20brand%20placement%2C%20diplomatic%20zone%20branding%2C%20CEO-only%20lounge%20with%20premium%20displays%2C%20invisible%20elite%20locations%20with%20sophisticated%20gold%20and%20marble%20design&width=800&height=500&seq=invitation-only-zones-2024&orientation=landscape'
    },
    {
      id: 'analytics',
      icon: <BarChart3 className="w-8 h-8" />,
      title: 'VVIP Analytics Vault',
      subtitle: 'Confidential Performance Reports',
      description: 'Exclusive access to demographic breakdowns, jet-by-jet exposure values, luxury audience psychographics, and spending behavior analytics available only to Brand Portfolio users.',
      features: [
        'Demographic breakdowns',
        'Jet-by-jet exposure values',
        'Luxury audience psychographics',
        'Spending behavior analytics',
        'Brand Portfolio exclusive access'
      ],
      confidential: true,
      image: 'https://readdy.ai/api/search-image?query=Confidential%20analytics%20vault%20interface%20showing%20VVIP%20demographic%20data%2C%20luxury%20audience%20psychographics%2C%20spending%20behavior%20charts%2C%20sophisticated%20data%20visualization%20with%20secure%20vault-like%20design%20and%20gold%20accents&width=800&height=500&seq=vvip-analytics-vault-2024&orientation=landscape'
    },
    {
      id: 'casestudies',
      icon: <Lock className="w-8 h-8" />,
      title: 'Ultra-Luxury Campaign Case Studies',
      subtitle: 'Confidential Edition Dossier',
      description: 'Private collection of high-end campaign case studies showing previous campaigns, measured impact, revenue uplift, and brand visibility increases in gold/black confidential dossier style.',
      features: [
        'Previous high-end campaigns',
        'Measured impact analysis',
        'Revenue uplift documentation',
        'Brand visibility increase metrics',
        'Gold/black confidential dossier'
      ],
      confidential: true,
      image: 'https://readdy.ai/api/search-image?query=Confidential%20luxury%20campaign%20case%20study%20dossier%20with%20gold%20and%20black%20design%2C%20high-end%20brand%20performance%20metrics%2C%20revenue%20uplift%20charts%2C%20sophisticated%20document%20presentation%20with%20classified%20styling%20and%20premium%20finishes&width=800&height=500&seq=luxury-case-studies-2024&orientation=landscape'
    }
  ];

  const currentFeatures = portfolioType === 'elite' ? eliteFeatures : brandFeatures;
  const pageTitle = portfolioType === 'elite' ? 'Elite Portfolio' : 'Brand Portfolio';
  const pageSubtitle = portfolioType === 'elite' 
    ? 'Ultra-Exclusive VVIP Aviation Marketing Solutions'
    : 'Premium Brand Placement in Private Aviation';

  const handleFeatureClick = (feature: any) => {
    setSelectedFeature(feature);
    setModalType('feature');
  };

  const closeModal = () => {
    setModalType('none');
    setSelectedFeature(null);
  };

  const handleBackToPortal = () => {
    navigate('/portal');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-slate-800 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 border-4 border-gold-400 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
          <h2 className="text-2xl font-bold text-white mb-2">Accessing {pageTitle}</h2>
          <p className="text-gray-400">Verifying elite credentials...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-slate-800">
      {/* Header */}
      <header className="relative py-8 border-b border-gold-400/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <button 
              onClick={handleBackToPortal}
              className="flex items-center gap-3 text-gold-400 hover:text-gold-300 transition-colors cursor-pointer"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back to Portal</span>
            </button>
            
            <div className="text-center">
              <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">{pageTitle}</h1>
              <p className="text-gold-400 font-medium">{pageSubtitle}</p>
            </div>
            
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-gold-400" />
              <span className="text-sm text-gray-400">Confidential Access</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Introduction */}
          <div className="text-center mb-16">
            <div className="inline-block px-6 py-3 rounded-full bg-gradient-to-r from-gold-500/20 to-yellow-600/20 backdrop-blur-sm border border-gold-400/30 mb-6">
              <span className="text-gold-400 font-semibold text-sm tracking-wider">
                {portfolioType === 'elite' ? 'ULTRA-EXCLUSIVE ACCESS' : 'PREMIUM BRAND SOLUTIONS'}
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-6 leading-tight">
              {portfolioType === 'elite' 
                ? 'Unprecedented Private Aviation Marketing Platform'
                : 'Luxury Brand Placement in Elite Aviation Network'
              }
            </h2>
            <p className="text-lg sm:text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              {portfolioType === 'elite'
                ? 'Access revolutionary marketing solutions that no other private jet service worldwide can offer. These breakthrough features are exclusively available to our elite clientele.'
                : 'Enter the private luxury boardroom reserved for the world\'s top companies. Experience precision targeting and unmatched brand exposure in the ultra-luxury aviation sector.'
              }
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12">
            {currentFeatures.map((feature, index) => (
              <div 
                key={feature.id}
                onClick={() => handleFeatureClick(feature)}
                className="group relative bg-gradient-to-br from-white/5 to-white/2 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-gold-400/50 transition-all duration-500 hover:bg-white/10 cursor-pointer transform hover:-translate-y-2 hover:shadow-2xl"
              >
                {/* Confidential Badge */}
                {feature.confidential && (
                  <div className="absolute top-4 right-4 bg-red-600/20 backdrop-blur-sm rounded-full px-3 py-1 border border-red-400/30">
                    <div className="flex items-center gap-2">
                      <Lock className="w-3 h-3 text-red-400" />
                      <span className="text-xs text-red-400 font-medium">CONFIDENTIAL</span>
                    </div>
                  </div>
                )}

                {/* Feature Icon */}
                <div className="w-16 h-16 bg-gradient-to-br from-gold-400 to-yellow-500 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <div className="text-black">
                    {feature.icon}
                  </div>
                </div>

                {/* Feature Content */}
                <div className="space-y-4">
                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold text-white mb-2 group-hover:text-gold-400 transition-colors">
                      {feature.title}
                    </h3>
                    <h4 className="text-sm sm:text-base text-gold-400 font-medium mb-3">
                      {feature.subtitle}
                    </h4>
                    <p className="text-gray-300 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>

                  {/* Feature Highlights */}
                  <div className="space-y-2">
                    {feature.features.slice(0, 3).map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-gray-400">
                        <div className="w-1.5 h-1.5 bg-gold-400 rounded-full"></div>
                        <span>{item}</span>
                      </div>
                    ))}
                    {feature.features.length > 3 && (
                      <div className="text-xs text-gold-400 font-medium">
                        +{feature.features.length - 3} more exclusive features
                      </div>
                    )}
                  </div>

                  {/* View Details Button */}
                  <div className="pt-4">
                    <div className="inline-flex items-center gap-2 text-gold-400 font-medium text-sm group-hover:text-gold-300 transition-colors">
                      <span>View Full Details</span>
                      <ArrowLeft className="w-4 h-4 rotate-180 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>

                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-gold-400/5 to-yellow-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
              </div>
            ))}
          </div>

          {/* Call to Action */}
          <div className="text-center mt-16 sm:mt-20">
            <div className="bg-gradient-to-br from-gold-600/10 to-yellow-600/10 backdrop-blur-sm rounded-2xl p-8 sm:p-12 border border-gold-400/20">
              <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                Ready to Access These Exclusive Solutions?
              </h3>
              <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
                Schedule a confidential consultation to discuss how these breakthrough features can elevate your brand in the ultra-luxury aviation market.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button 
                  onClick={() => navigate('/portal?modal=consultation')}
                  className="bg-gradient-to-r from-gold-500 to-yellow-600 text-black px-8 py-4 rounded-xl font-bold hover:shadow-2xl transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer"
                >
                  Schedule Confidential Consultation
                </button>
                <button 
                  onClick={handleBackToPortal}
                  className="border-2 border-gold-400/50 text-gold-400 px-8 py-4 rounded-xl font-semibold hover:bg-gold-400/10 transition-all whitespace-nowrap cursor-pointer backdrop-blur-sm"
                >
                  Return to Portal
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Feature Detail Modal */}
      {modalType === 'feature' && selectedFeature && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6 z-50">
          <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl p-8 max-w-6xl w-full max-h-[90vh] overflow-y-auto border border-gold-400/30">
            <div className="flex justify-between items-start mb-8">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-gold-400 to-yellow-500 rounded-full flex items-center justify-center">
                  <div className="text-black text-lg">
                    {selectedFeature.icon}
                  </div>
                </div>
                <div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-white">{selectedFeature.title}</h2>
                  <p className="text-gold-400 font-medium">{selectedFeature.subtitle}</p>
                </div>
              </div>
              <button 
                onClick={closeModal}
                className="text-gray-400 hover:text-white cursor-pointer text-2xl"
              >
                ✕
              </button>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-white mb-4">Exclusive Features</h3>
                  <div className="space-y-3">
                    {selectedFeature.features.map((feature: string, idx: number) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-white/5 rounded-lg">
                        <div className="w-2 h-2 bg-gold-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-gray-300">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-gold-600/10 to-yellow-600/10 rounded-xl p-6 border border-gold-400/20">
                  <h4 className="text-lg font-semibold text-gold-400 mb-3">Exclusive Access</h4>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    {selectedFeature.description}
                  </p>
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="aspect-video bg-gradient-to-br from-gold-600/20 to-yellow-600/20 rounded-xl overflow-hidden border border-gold-400/30">
                  <img
                    src={selectedFeature.image}
                    alt={selectedFeature.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                
                <div className="flex flex-col gap-3">
                  <button 
                    onClick={() => {
                      closeModal();
                      navigate('/portal?modal=consultation');
                    }}
                    className="bg-gradient-to-r from-gold-500 to-yellow-600 text-black px-6 py-3 rounded-lg font-bold hover:shadow-xl transition-all whitespace-nowrap cursor-pointer"
                  >
                    Request Access to This Feature
                  </button>
                  <button 
                    onClick={closeModal}
                    className="border border-gray-600 text-gray-300 px-6 py-3 rounded-lg font-medium hover:bg-gray-800 transition-all whitespace-nowrap cursor-pointer"
                  >
                    Close Details
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ElitePortfolioPage;